import React, { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { colors } from '../constants/colors';
import { Icons } from '../constants/icons';
import { createRipple } from '../utils/animations';
import StarRating from './StarRating';

/**
 * Menu grid product card.
 * Extracted from ChamomileApp.jsx (monolith break-up, Aug 2026).
 *
 * `variants` is set for sized products (e.g. Tee S/M/L/XL): they render as ONE
 * card with a size selector, and the selected size's real product drives price,
 * cart and reviews.
 *
 * `enforceStock` is passed for merch/food only — physical pieces the POS
 * counts. Drinks are brewed to order and never stock-gated. A missing stock
 * field means "unknown" and never blocks a sale.
 */
const ProductCard = ({ product, quantity, onAdd, onRemove, rating, reviewCount, onReviewClick, index = 0, onFirstAdd, variants = null, cart = null, onSelectVariant = null, displayName = null, cornerEcho = null, enforceStock = false }) => {
  const [isHovered, setIsHovered] = useState(false);
  const prevQuantity = useRef(quantity);

  const stockNum = typeof product.stock === 'number' ? product.stock : null;
  const soldOut = enforceStock && stockNum === 0;
  // Also covers a cart that was filled before the item sold out (qty ≥ 0).
  const atMax = enforceStock && stockNum !== null && quantity >= stockNum;

  const handleAdd = (e) => {
    e.stopPropagation();
    if (soldOut || atMax) return;
    // Ripple effect on button
    if (typeof createRipple === 'function') createRipple(e);

    // Check if this is the first item being added
    if (prevQuantity.current === 0 && onFirstAdd) {
      onFirstAdd();
    }
    prevQuantity.current = (quantity || 0) + 1;
    onAdd();
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{
        duration: 0.4, delay: index * 0.08,
        type: "spring", stiffness: 300, damping: 24
      }}
      style={{
        display: 'flex', flexDirection: 'column', gap: '12px', padding: '12px',
        background: '#FFFFFF',
        // `cornerEcho` exaggerates the one outer top corner that sits under the
        // category dome's descending flank, so the first row continues the arc.
        borderRadius: cornerEcho === 'left' ? '30px 16px 16px 16px'
          : cornerEcho === 'right' ? '16px 30px 16px 16px'
          : '16px',
        border: '1px solid rgba(0,0,0,0.05)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.03)',
        position: 'relative', overflow: 'hidden', cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered ? 'translateY(-2px)' : 'none',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      whileTap={{ scale: 0.98 }}
    >
      {/* Top Half: Image Area */}
      <div style={{
        width: '100%', aspectRatio: '1', background: '#FFFFFF', borderRadius: '12px',
        display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative'
      }}>
        {/* Quantity Badge */}
        <AnimatePresence>
          {quantity > 0 && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
              transition={{ type: "spring", stiffness: 500, damping: 20 }}
              style={{
                position: 'absolute', top: '8px', right: '8px',
                background: `linear-gradient(135deg, ${colors.gold400}, ${colors.gold500})`,
                color: '#fff', width: '24px', height: '24px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '11px', fontWeight: 700, boxShadow: '0 2px 8px rgba(201,169,98,0.4)',
                zIndex: 10,
              }}
            >
              {quantity}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Sold-out badge — merch/food with 0 POS stock */}
        {soldOut && (
          <div style={{
            position: 'absolute', top: '8px', left: '8px', zIndex: 10,
            background: colors.stone800, color: '#fff', padding: '3px 10px',
            borderRadius: '999px', fontSize: '10px', fontWeight: 700,
            letterSpacing: '0.4px', textTransform: 'uppercase',
          }}>
            Sold out
          </div>
        )}

        <motion.div
          animate={{ scale: isHovered && !soldOut ? 1.08 : 1, rotate: isHovered && !soldOut ? 2 : 0 }}
          transition={{ duration: 0.3 }}
          style={{ width: '95%', height: '95%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <img
            src={product.imageUrl}
            alt={product.name}
            referrerPolicy="no-referrer"
            loading="lazy"
            decoding="async"
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              opacity: soldOut ? 0.4 : 1,
              filter: soldOut ? 'grayscale(0.7)' : 'none',
              transition: 'opacity 0.2s ease, filter 0.2s ease',
            }}
          />
        </motion.div>

      </div>

      {/* Bottom Half: Info + Action */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', flex: 1, textAlign: 'center' }}>
        <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#1f2937', margin: 0, lineHeight: 1.2 }}>
          {displayName || product.name}
        </h3>

        {/* Review rating snippet — cards without reviews show nothing rather
            than a grid full of "No reviews yet" (minimalist choice, Aug 2026) */}
        {rating > 0 && (
          <div
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px', cursor: 'pointer' }}
            onClick={(e) => { e.stopPropagation(); onReviewClick && onReviewClick(); }}
          >
            <StarRating rating={rating} count={reviewCount} size={11} showCount={false} onClick={onReviewClick} />
          </div>
        )}

        {/* Size selector — only for grouped sized products (e.g. Tee S/M/L/XL) */}
        {variants && variants.length > 1 && (
          <div style={{ width: '100%', marginTop: '6px' }}>
            <div style={{
              fontSize: '10px', fontWeight: 600, color: colors.stone500,
              textAlign: 'left', marginBottom: '5px', letterSpacing: '0.3px',
            }}>
              SIZE
            </div>
            <div
              role="radiogroup"
              aria-label={`Select size for ${displayName || product.name}`}
              style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', justifyContent: 'flex-start' }}
            >
              {variants.map(v => {
                const isSelected = v.id === product.id;
                const inCart = cart ? (cart[v.id] || 0) : 0;
                // Dimmed but still selectable — picking a sold-out size shows
                // the card's sold-out state instead of a mystery dead button.
                const sizeSoldOut = enforceStock && typeof v.stock === 'number' && v.stock === 0;
                return (
                  <button
                    key={v.id}
                    role="radio"
                    aria-checked={isSelected}
                    aria-label={`Size ${v.size}${sizeSoldOut ? ', sold out' : ''}${inCart > 0 ? `, ${inCart} in cart` : ''}`}
                    onClick={(e) => { e.stopPropagation(); onSelectVariant && onSelectVariant(v.id); }}
                    style={{
                      position: 'relative',
                      minWidth: '34px', padding: '5px 8px',
                      borderRadius: '8px', cursor: 'pointer',
                      fontFamily: 'inherit', fontSize: '11px', fontWeight: 700,
                      background: isSelected ? colors.gold400 : '#fff',
                      color: isSelected ? '#fff' : sizeSoldOut ? colors.stone300 : colors.stone600,
                      border: `1.5px solid ${isSelected ? colors.gold400 : colors.stone200}`,
                      textDecoration: sizeSoldOut ? 'line-through' : 'none',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    {v.size}
                    {inCart > 0 && (
                      <span style={{
                        position: 'absolute', top: '-5px', right: '-5px',
                        background: isSelected ? '#fff' : colors.gold500,
                        color: isSelected ? colors.gold600 : '#fff',
                        border: `1.5px solid ${colors.gold400}`,
                        borderRadius: '999px', minWidth: '15px', height: '15px',
                        fontSize: '9px', fontWeight: 700, lineHeight: '12px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        padding: '0 3px',
                      }}>
                        {inCart}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div style={{ flex: 1 }}></div>

        {/* Bottom Row: Price and Cart Button */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px', width: '100%' }}>
          <p style={{ fontSize: '16px', fontWeight: 800, color: '#1f2937', margin: 0, textAlign: 'left' }}>
            <span style={{ fontSize: '11px', fontWeight: 600, color: colors.gold500 }}>RM </span>
            {product.price.toFixed(2)}
          </p>

          {/* Cart+ Button Pattern */}
          {quantity > 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', background: '#f3f4f6', borderRadius: '100px', padding: '2px' }}>
              <button
                onClick={(e) => { e.stopPropagation(); createRipple(e); onRemove(); }}
                aria-label={`Remove one ${displayName || product.name} from cart`}
                style={{ background: '#fff', border: 'none', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', color: colors.stone600, fontSize: '16px', fontWeight: 600 }}
              >
                −
              </button>
              <span style={{ width: '24px', textAlign: 'center', fontSize: '13px', fontWeight: 700, color: colors.stone800 }}>
                {quantity}
              </span>
              <button
                onClick={handleAdd}
                disabled={atMax}
                aria-label={atMax ? `${displayName || product.name}: no more stock available` : `Add one more ${displayName || product.name}`}
                style={{ background: atMax ? colors.stone300 : colors.gold400, border: 'none', width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: atMax ? 'not-allowed' : 'pointer', boxShadow: atMax ? 'none' : '0 2px 4px rgba(212,168,83,0.3)', color: '#fff', fontSize: '16px', fontWeight: 600 }}
              >
                +
              </button>
            </div>
          ) : soldOut ? (
            <span style={{
              fontSize: '11px', fontWeight: 700, color: colors.stone400,
              textTransform: 'uppercase', letterSpacing: '0.4px',
            }}>
              Sold out
            </span>
          ) : (
            <button
              onClick={handleAdd}
              aria-label={`Add ${displayName || product.name} to cart`}
              style={{
                background: colors.black50, color: '#fff', border: 'none',
                width: '32px', height: '32px', borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.2s', boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
              }}
            >
              <Icons.Plus size={16} />
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default ProductCard;
